import { auth } from "@lumberset/api-client";
import { Button, Field, Heading, Hint, Input, toast } from "@lumberset/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";

interface SetPasswordSearch {
  token?: string;
  /** Set by better-auth when the link was used or has run out. */
  error?: string;
  /** Set on invitation links, so the page can welcome someone new. */
  welcome?: boolean;
  /** The account an Admin sent the link for. Links from "Forgot your password?" do not say. */
  for?: string;
}

/**
 * Where invitation and password reset links land. better-auth checks the link
 * first and passes the token on; this page asks for the password twice. The
 * sign-in frame lets a signed-in person through to here, and this page makes
 * sure the new password does not go to the wrong account.
 */
export const Route = createFileRoute("/_auth/set-password")({
  validateSearch: (search: Record<string, unknown>): SetPasswordSearch => ({
    token:
      typeof search.token === "string" || typeof search.token === "number"
        ? String(search.token)
        : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
    welcome: search.welcome === 1 || search.welcome === "1" || search.welcome === true || undefined,
    for: typeof search.for === "string" && search.for ? search.for : undefined,
  }),
  /** Who is signed in on this browser, if anyone. */
  loader: async () => {
    const { data } = await auth.getSession();
    return { signedIn: data ? { id: data.user.id, email: data.user.email } : null };
  },
  component: SetPassword,
});

function SetPassword() {
  const { token, error: linkError, welcome, for: linkFor } = Route.useSearch();
  const { signedIn } = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!token || linkError) {
    return (
      <div className="flex flex-col gap-y-4">
        <Heading>This link no longer works</Heading>
        <p className="txt-small text-ui-fg-subtle">
          {welcome
            ? "Invitation links work once, for 7 days. Get a fresh link below, or ask the Admin who invited you to send the invitation again."
            : "Password links work once, for one hour. Ask for a new one."}
        </p>
        <Button asChild variant="secondary" size="large" block>
          <Link to="/forgot-password">Send a new link</Link>
        </Button>
      </div>
    );
  }

  async function signOut() {
    await auth.signOut();
    queryClient.clear();
  }

  // The link names someone else: they choose to sign out before going on.
  if (signedIn && linkFor && linkFor !== signedIn.id) {
    return (
      <div className="flex flex-col gap-y-4">
        <Heading>{welcome ? "Welcome to the LumberSet team" : "Choose a new password"}</Heading>
        <p className="txt-small text-ui-fg-subtle">
          You are signed in as <span className="txt-small-plus break-words">{signedIn.email}</span>.
          Sign out to set a password for another account.
        </p>
        <Button
          size="large"
          block
          isLoading={busy}
          onClick={async () => {
            setBusy(true);
            await signOut();
            await router.invalidate();
            setBusy(false);
          }}
        >
          Sign out
        </Button>
      </div>
    );
  }

  /** Signed in as the very person the link is for: they stay signed in. */
  const same = signedIn !== null && linkFor === signedIn.id;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    if (password !== String(form.get("again") ?? "")) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await auth.resetPassword({ newPassword: password, token: token! });
    if (result.error) {
      setBusy(false);
      setError(
        result.error.code === "INVALID_TOKEN"
          ? "This link no longer works. Ask for a new one."
          : (result.error.message ?? "That did not work. Try again."),
      );
      return;
    }
    if (same) {
      toast.success("Your new password is saved");
      await navigate({ to: "/" });
      return;
    }
    // The link does not say whose it is, so whoever was signed in here is signed out.
    if (signedIn) await signOut();
    await navigate({ to: "/login", search: { notice: "password-set" } });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-5">
      <div>
        <Heading>{welcome ? "Welcome to the LumberSet team" : "Choose a new password"}</Heading>
        <p className="txt-small mt-1 text-ui-fg-subtle">
          {welcome
            ? "Choose a password to finish setting up your account. You sign in with it and your email."
            : "You sign in with it and your email."}
        </p>
      </div>

      {signedIn && !same ? (
        <p className="txt-small rounded-lg bg-ui-bg-subtle px-3 py-2 text-ui-fg-base" role="status">
          You are signed in as <span className="txt-small-plus break-words">{signedIn.email}</span>.
          Saving signs you out, so you can then sign in with the new password.
        </p>
      ) : null}

      <Field label="Password" hint="At least 10 characters.">
        {(control) => (
          <Input
            {...control}
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            maxLength={128}
            required
            size="large"
            autoFocus
          />
        )}
      </Field>

      <Field label="Password again">
        {(control) => (
          <Input
            {...control}
            name="again"
            type="password"
            autoComplete="new-password"
            minLength={10}
            maxLength={128}
            required
            size="large"
          />
        )}
      </Field>

      {error ? <Hint variant="error">{error}</Hint> : null}

      <Button type="submit" size="large" block isLoading={busy}>
        Save password
      </Button>
    </form>
  );
}
